import { errorWords } from "@alm/domain";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import type { Permission } from "@alm/policy";
import { EmptyState } from "@/components/app/lists/empty-state";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { orpc } from "@/lib/orpc";
import { guardRoute } from "@/lib/policy/route-guard";
import {
  NativeRoleCard,
  reachOf,
} from "@/routes/_authenticated/_administration/settings/roles/-components/native-role-card";
import { RoleEditor } from "@/routes/_authenticated/_administration/settings/roles/-components/role-editor";

export const Route = createFileRoute("/_authenticated/platform_/roles/$roleId")({
  beforeLoad: ({ context }) => guardRoute(context.me, "platform-console"),
  staticData: { fullWidth: true },
  component: NativeRolePage,
});

type Save = { roleId: string; name: string; description: string; permissions: Permission[] };

/** One native role, edited for every organization at once, with a confirmation that says so. */
function NativeRolePage() {
  const { roleId } = Route.useParams();
  const queryClient = useQueryClient();
  const list = useQuery(orpc.administration.roles.native.list.queryOptions({}));
  const grantable = useQuery(orpc.administration.roles.native.permissions.queryOptions({}));
  const [confirmSave, setConfirmSave] = useState<Save | null>(null);
  const update = useMutation(
    orpc.administration.roles.native.update.mutationOptions({
      onSuccess: async (saved) => {
        toast.success(`${saved.name} saved for every organization.`);
        await queryClient.invalidateQueries({ queryKey: orpc.administration.roles.key() });
      },
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  if (list.isPending || grantable.isPending) return <Skeleton className="h-96 w-full" />;
  if (list.isError || grantable.isError) {
    const failed = list.isError ? list : grantable;
    return (
      <ReadFailed
        message="The native role could not be loaded."
        error={failed.error}
        retrying={failed.isFetching}
        onRetry={() => void failed.refetch()}
      />
    );
  }
  const role = list.data.find((row) => row.id === roleId);
  if (!role) {
    return (
      <EmptyState
        title="No such native role"
        description="It may have been deleted."
        action={
          <Button asChild variant="outline">
            <Link to="/platform/roles">All native roles</Link>
          </Button>
        }
      />
    );
  }
  const reach = reachOf(role);

  return (
    <>
      {confirmSave ? (
        <RemoveConfirm
          title={`Save ${role.name} for every organization?`}
          description={`This changes what ${reach} may do, at once.`}
          confirmLabel="Save"
          destructive={false}
          onConfirm={() => {
            update.mutate(confirmSave);
            setConfirmSave(null);
          }}
          onClose={() => setConfirmSave(null)}
        />
      ) : null}
      <RoleEditor
        key={`${role.id}-${String(role.updatedAt)}`}
        title={role.name}
        initial={{
          name: role.name,
          description: role.description ?? "",
          permissions: role.permissions,
        }}
        grantable={grantable.data as Permission[]}
        pending={update.isPending}
        headerBadge={
          <Badge variant="outline" className="font-normal">
            Native, every organization
          </Badge>
        }
        aside={<NativeRoleCard roleId={role.id} name={role.name} reach={reach} />}
        place="console"
        onSave={(values) => setConfirmSave({ roleId: role.id, ...values })}
      />
    </>
  );
}
